/**
 * `<OrgWorkspacesRoute>` — `/w/:slug/organization/workspaces`
 *
 * Org-admin overview of every workspace in the caller's organization, with its
 * environment, onboarding state, and active-member count. Each row links into
 * the workspace (`/w/:slug`) — org admins can already enter any workspace in
 * their org via `WorkspaceContextGuard`, so the links resolve.
 *
 * Org-scoped data (`/api/org/workspaces`); lives under `/w/:slug` only to reuse
 * the settings shell. A scoped-down sibling of the FDE staff console directory.
 */

import { authClient } from "@cometchat-ai/auth/client";
import { Button, TableBody, TableHeader } from "@cometchat-ai/ui-dashboard";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Network } from "../components/icons";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import {
  DataGrid,
  DataGridCell,
  DataGridHead,
  DataGridHeaderRow,
  DataGridRow,
} from "../components/data-grid";
import { PageShell } from "../components/page-shell";
import { EmptyState } from "../components/empty-state";
import { ErrorState } from "../components/error-state";
import { ListSkeleton } from "../components/list-skeleton";
import { RolePill } from "../components/settings-parts";

export function OrgWorkspacesRoute() {
  const { t } = useTranslation("workspace");
  const navigate = useNavigate();

  const workspacesQuery = useQuery({
    queryKey: ["org-workspaces"],
    queryFn: () => authClient.listOrgWorkspaces(),
  });

  const workspaces = workspacesQuery.data ?? [];
  const count = workspaces.length;

  const countLabel = useMemo(
    () =>
      count === 1
        ? t("organization.workspaces.countOne")
        : t("organization.workspaces.countOther", { count }),
    [count, t],
  );

  return (
    <PageShell
      data-testid="org-workspaces"
      className="p-0"
      title={t("organization.workspaces.title")}
      description={t("organization.workspaces.subtitle")}
    >
      {workspacesQuery.isPending ? (
        <ListSkeleton
          rows={5}
          itemClassName="h-10 w-full"
          className="space-y-2 p-6"
          data-testid="org-workspaces-skeleton"
        />
      ) : workspacesQuery.error ? (
        <div className="px-6 py-12">
          <ErrorState message={workspacesQuery.error.message} onRetry={workspacesQuery.refetch} />
        </div>
      ) : workspaces.length === 0 ? (
        <div className="px-6 py-12">
          <EmptyState
            data-testid="org-workspaces-empty"
            icon={Network}
            title={t("organization.workspaces.title")}
            description={t("organization.workspaces.empty")}
          />
        </div>
      ) : (
        <>
          <div className="flex items-center px-6 py-2.5">
            <span className="text-[13px] text-muted-foreground" data-testid="org-workspaces-count">
              {countLabel}
            </span>
          </div>
          <DataGrid data-testid="org-workspaces-table">
            <TableHeader>
              <DataGridHeaderRow>
                <DataGridHead className="pl-6 pr-4">
                  {t("organization.workspaces.table.name")}
                </DataGridHead>
                <DataGridHead className="w-36">
                  {t("organization.workspaces.table.environment")}
                </DataGridHead>
                <DataGridHead className="w-40">
                  {t("organization.workspaces.table.onboarding")}
                </DataGridHead>
                <DataGridHead className="w-28">
                  {t("organization.workspaces.table.members")}
                </DataGridHead>
                <DataGridHead className="w-28 pr-6" divider={false} />
              </DataGridHeaderRow>
            </TableHeader>
            <TableBody>
              {workspaces.map((ws) => (
                <DataGridRow
                  key={ws.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/w/${ws.slug}`)}
                  data-testid={`org-workspace-row-${ws.slug}`}
                >
                  <DataGridCell className="pl-6 pr-4">
                    <div className="min-w-0">
                      <div className="truncate text-[13px] font-medium text-foreground">
                        {ws.name}
                      </div>
                      <div className="truncate text-[13px] text-muted-foreground">{ws.slug}</div>
                    </div>
                  </DataGridCell>
                  <DataGridCell>
                    <RolePill role={ws.environment} label={ws.environment} />
                  </DataGridCell>
                  <DataGridCell className="capitalize">{ws.onboardingState}</DataGridCell>
                  <DataGridCell className="tabular-nums">{ws.memberCount}</DataGridCell>
                  <DataGridCell
                    className="pr-6 text-right"
                    divider={false}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 gap-1 rounded-md text-[13px]"
                      onClick={() => navigate(`/w/${ws.slug}`)}
                      data-testid={`org-workspace-open-${ws.slug}`}
                    >
                      {t("organization.workspaces.open")}
                      <ChevronRight
                        className="h-3.5 w-3.5 transition-transform group-hover/row:translate-x-0.5"
                        aria-hidden="true"
                      />
                    </Button>
                  </DataGridCell>
                </DataGridRow>
              ))}
            </TableBody>
          </DataGrid>
        </>
      )}
    </PageShell>
  );
}
